import React, { useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { useConfigurationData } from './hooks/useConfigurationData';
import { useResizableSidebar } from './hooks/useResizableSidebar';
import { useConfigurationService } from './services/configurationService';
import { useErrorHandler } from '../utils/useErrorHandler';
import { findSelectedItem } from './utils/configurationUtils';
import { ResizeHandle } from '../conf/constants';
import ConfigSidebar from './components/ConfigSidebar';
import ConfigEditor from './components/ConfigEditor';

const ConfigurationManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const { 
    snackbar, 
    handleSnackbarClose, 
    showSuccessMessage, 
    showErrorMessage, 
    handleErrorWithLogout 
  } = useErrorHandler();
  
  const {
    configGroups,
    selectedItem,
    setSelectedItem,
    configState,
    setConfigState,
    isUpdating,
    setIsUpdating,
  } = useConfigurationData({
    showSuccessMessage,
    showErrorMessage,
    handleErrorWithLogout,
  });

  const { sidebarWidth, isResizing, containerRef, handleMouseDown } = useResizableSidebar('30%');
  const { updateConfiguration } = useConfigurationService();

  const selectedConfig = findSelectedItem(configGroups, selectedItem);

  const handleUpdate = async () => {
    if (!selectedConfig) return;
    
    setIsUpdating(true);
    try {
      await updateConfiguration(
        selectedItem,
        configGroups,
        configState,
        showSuccessMessage,
        handleErrorWithLogout
      );
    } catch (error) {
      // Error handled in service
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box 
      sx={{ display: 'flex', height: '100%' }} 
      ref={containerRef} 
      style={{ cursor: isResizing ? 'col-resize' : 'auto' }}
    >
      <ConfigSidebar
        configGroups={configGroups}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sidebarWidth={sidebarWidth}
      />

      <ResizeHandle onMouseDown={handleMouseDown} />

      {selectedConfig && (
        <ConfigEditor
          selectedConfig={selectedConfig}
          configState={configState}
          setConfigState={setConfigState}
          isUpdating={isUpdating}
          onUpdate={handleUpdate}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigurationManagement;